// The limitation a link writes — Based On · Period · Warning · Warning Unit ·
// Limitation Notes — as legacy's limitation editor lays it out
// (mod_aircraft_templates/includes/aircraft_templates_inspection_update.inc:60-90).
//
// Shared by the two "link" dialogs that write one: the aircraft configuration's
// template tabs (link a template to a MODEL) and the inspection record's
// Aircraft List (link AIRCRAFT to an inspection). Renders Grid ITEMS — the
// caller owns the dialog and its Grid container.
//
// Based On is free-typed with suggestions: the stored vocabulary is not uniform
// across tenants (g2 holds "Engine 1 Hours", legacy's default list says
// "Engine1 Hours").
import React, { useMemo } from "react";
import { Autocomplete, Grid, TextField } from "@mui/material";

export interface LimitationFieldValues {
  based_on: string;
  period: string;
  warning: string;
  warning_unit: string;
  limitation_notes: string;
}

export const BLANK_LIMITATION: LimitationFieldValues = {
  based_on: "",
  period: "",
  warning: "",
  warning_unit: "",
  limitation_notes: "",
};

/** One Based On suggestion: the stored value and what the user reads. */
export interface BasedOnChoice {
  value: string;
  label: string;
}

/** The form as a request body: a blank field is sent as null. */
export function limitationBody(v: LimitationFieldValues) {
  return {
    based_on: v.based_on || null,
    period: v.period === "" ? null : v.period,
    warning: v.warning === "" ? null : v.warning,
    warning_unit: v.warning_unit || null,
    limitation_notes: v.limitation_notes || null,
  };
}

/**
 * Based On and Period are required on a link, as legacy's limitation editor
 * requires them (*_inspection_update.inc:409-411; Aaron 2026-10-06). Returns the
 * message to show, or null. The backend refuses the same (422) — this only says
 * so before the round trip.
 */
export function missingInterval(v: LimitationFieldValues): string | null {
  return !v.based_on.trim() || v.period === "" ? "Based On and Period are required" : null;
}

/** A stored value as a form value: null/undefined read as blank. */
export const formValue = (v: unknown) => (v === null || v === undefined ? "" : String(v));

interface Props {
  value: LimitationFieldValues;
  onChange: (next: LimitationFieldValues) => void;
  basedOnOptions: BasedOnChoice[];
  /** Marks Based On and Period required (see missingInterval). */
  required?: boolean;
}

export default function LimitationFields({ value, onChange, basedOnOptions, required = false }: Props) {
  const basedOnValues = useMemo(() => basedOnOptions.map((o) => o.value), [basedOnOptions]);
  const labelFor = (v: string) => basedOnOptions.find((o) => o.value === v)?.label ?? v;
  const set = (key: keyof LimitationFieldValues) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...value, [key]: e.target.value });

  return (
    <>
      <Grid item xs={12} md={6}>
        <Autocomplete
          freeSolo
          options={basedOnValues}
          getOptionLabel={(v) => labelFor(String(v))}
          value={value.based_on || null}
          onChange={(_, v) => onChange({ ...value, based_on: (v as string) ?? "" })}
          onInputChange={(_, v, reason) => {
            if (reason === "input") onChange({ ...value, based_on: v });
          }}
          renderInput={(params) => <TextField {...params} label="Based On" required={required} />}
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <TextField
          fullWidth
          type="number"
          label="Period"
          required={required}
          value={value.period}
          onChange={set("period")}
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <TextField fullWidth type="number" label="Warning" value={value.warning} onChange={set("warning")} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Warning Unit" value={value.warning_unit} onChange={set("warning_unit")} />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Limitation Notes"
          value={value.limitation_notes}
          onChange={set("limitation_notes")}
        />
      </Grid>
    </>
  );
}
